<script setup lang="ts">
import { computed } from 'vue'
import type { InputText } from '../types'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useCardContext } from '../composables/useCardContext'
import { spacingClasses } from '../utils/classes'

const props = defineProps<{ element: InputText }>()

const { inputs, errors } = useCardContext()

const inputType = computed(() => {
  const style = props.element.style?.toLowerCase()
  if (style === 'tel') return 'tel'
  if (style === 'url') return 'url'
  if (style === 'email') return 'email'
  if (style === 'password') return 'password'
  return 'text'
})

function handleInput(value: string | number) {
  inputs[props.element.id] = String(value ?? '')
  if (errors[props.element.id]) {
    errors[props.element.id] = ''
  }
}
</script>

<template>
  <div :class="cn('flex flex-col gap-1.5', spacingClasses[props.element.spacing ?? 'default'])">
    <Label v-if="props.element.label" :for="props.element.id">
      {{ props.element.label }}
      <span v-if="props.element.isRequired" class="text-destructive">*</span>
    </Label>
    <Textarea
      v-if="props.element.isMultiline"
      :id="props.element.id"
      :placeholder="props.element.placeholder"
      :maxlength="props.element.maxLength"
      :model-value="inputs[props.element.id] ?? props.element.value ?? ''"
      :class="cn('bg-background', errors[props.element.id] && 'border-destructive')"
      @update:model-value="handleInput"
    />
    <Input
      v-else
      :id="props.element.id"
      :type="inputType"
      :placeholder="props.element.placeholder"
      :maxlength="props.element.maxLength"
      :model-value="inputs[props.element.id] ?? props.element.value ?? ''"
      :class="cn('bg-background', errors[props.element.id] && 'border-destructive')"
      @update:model-value="handleInput"
    />
    <p v-if="errors[props.element.id]" class="text-sm text-destructive">
      {{ errors[props.element.id] }}
    </p>
  </div>
</template>
